import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { api, money, fmtDate } from "@/lib/api";
import { Card, PageHeader, Empty } from "@/components/common";

const CARDS = [["total_leads", "Total leads"], ["new_enquiries", "New enquiries"], ["contacted", "Contacted leads"], ["qualified", "Qualified prospects"], ["trial_requests", "Trial requests"],
  ["confirmed_trials", "Confirmed trials"], ["trials_completed", "Trials completed"], ["purchases", "Purchases completed"], ["returns_pending", "Returns pending"], ["revenue", "Total sales revenue"]];
const COLORS = ["#15803D", "#1E293B", "#F59E0B", "#0EA5E9", "#64748B", "#22C55E", "#E11D48"];

const ChartCard = ({ title, children, testid }) => (
  <Card className="p-5" data-testid={testid}><p className="mb-4 font-display font-semibold text-slate-900">{title}</p><div className="h-64">{children}</div></Card>
);

function Reminders() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/admin/reminders").then((x) => setR(x.data)).catch(() => {}); }, []);
  if (!r) return null;
  const groups = [
    ["Pending enquiries", r.pending_enquiries, (x) => [`/admin/customers/${x.id}`, `${x.first_name} ${x.last_name}`, `since ${fmtDate(x.created_at)}`]],
    ["Follow-ups due", r.due_follow_ups, (x) => [`/admin/customers/${x.id}`, `${x.first_name} ${x.last_name}`, `due ${fmtDate(x.next_follow_up)}`]],
    ["Upcoming trials", r.upcoming_trials, (x) => [`/admin/bookings/${x.id}`, `${x.reference} · ${x.customer_name}`, `starts ${fmtDate(x.trial_start)}`]],
    ["Overdue returns", r.overdue_returns, (x) => [`/admin/bookings/${x.id}`, `${x.reference} · ${x.customer_name}`, `ended ${fmtDate(x.trial_end)}`]],
    ["Post-trial purchase follow-ups", r.post_trial_followups, (x) => [`/admin/bookings/${x.id}`, `${x.reference} · ${x.customer_name}`, `ended ${fmtDate(x.trial_end)}`]],
  ];
  return (
    <Card className="p-5" data-testid="dashboard-reminders">
      <p className="font-display font-semibold text-slate-900">Reminders</p>
      <p className="text-xs text-slate-500">Thresholds configurable in Settings</p>
      <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {groups.map(([t, list, f]) => (
          <div key={t} className="rounded-lg bg-slate-50 p-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-600">{t} <span className="text-emerald-700">({list.length})</span></p>
            <ul className="mt-2 space-y-1.5">
              {list.slice(0, 5).map((x) => { const [to, a, b] = f(x); return <li key={to} className="text-sm"><Link to={to} className="font-medium text-slate-900 hover:text-emerald-800">{a}</Link><span className="block text-xs text-slate-500">{b}</span></li>; })}
              {!list.length && <li className="text-xs text-slate-400">Nothing due</li>}
            </ul>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <Empty>Loading dashboard…</Empty>;
  const conv = [{ name: "Purchased", value: d.conversion.purchased }, { name: "Returned / returning", value: d.conversion.returned }, { name: "Undecided", value: Math.max(d.conversion.trials_completed - d.conversion.purchased - d.conversion.returned, 0) }];
  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Live metrics calculated from your database records" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {CARDS.filter(([k]) => d.cards[k] !== null).map(([k, l]) => (
          <Card key={k} className="p-4" data-testid={`stat-card-${k}`}>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</p>
            <p className={`mt-2 font-display text-3xl font-bold ${k === "revenue" ? "text-emerald-700" : "text-slate-900"}`} data-testid={`stat-value-${k}`}>{k === "revenue" ? money(d.cards[k]) : d.cards[k]}</p>
          </Card>
        ))}
      </div>
      <div className="mt-6"><Reminders /></div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <ChartCard title="Leads by day (last 30 days)" testid="chart-leads-by-day">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}><LineChart data={d.leads_by_day}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Line type="monotone" dataKey="leads" stroke="#15803D" strokeWidth={2.5} dot={false} /></LineChart></ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Leads by month" testid="chart-leads-by-month">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}><BarChart data={d.leads_by_month}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="month" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Bar dataKey="leads" fill="#1E293B" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Leads by marketing source" testid="chart-leads-by-source">
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}><BarChart data={d.leads_by_source} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis type="number" allowDecimals={false} fontSize={11} /><YAxis type="category" dataKey="source" width={110} fontSize={11} /><Tooltip /><Bar dataKey="leads" fill="#15803D" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer>
        </ChartCard>
        <ChartCard title={`Trial-to-purchase conversion: ${d.conversion.rate}%`} testid="chart-conversion">
          {d.conversion.trials_completed ? (
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}><PieChart><Pie data={conv} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} label>{conv.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}</Pie><Tooltip /></PieChart></ResponsiveContainer>
          ) : <Empty>No completed trials yet</Empty>}
        </ChartCard>
        {d.sales_by_month && (
          <ChartCard title="Sales by month (successful payments)" testid="chart-sales-by-month">
            {d.sales_by_month.length ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}><BarChart data={d.sales_by_month}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="month" fontSize={11} /><YAxis fontSize={11} /><Tooltip formatter={(v) => money(v)} /><Bar dataKey="revenue" fill="#F59E0B" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
            ) : <Empty>No recorded sales yet</Empty>}
          </ChartCard>
        )}
      </div>
    </div>
  );
}
